import { defineComponent } from '@nuxtjs/composition-api';
import { mapState } from 'vuex';
import acceptConnectionRequestMutation from '@/apollo/mutations/Portfolio/AcceptConnectionRequest.mutation';
import cancelConnectionRequestMutation from '@/apollo/mutations/Portfolio/CancelConnectionRequest.mutation';
import connectionsQuery from '@/apollo/queries/Connections.query';
import createConnectionRequestMutation from '@/apollo/mutations/Portfolio/CreateConnectionRequest.mutation';
import currentUserUnseenNotificationsQuery from '@/components/global/HeaderMenu/HeaderMenuNotifications/CurrentUserUnseenNotifications.query';
import portfolioConnectionRequestsQuery from '@/apollo/queries/Portfolio/PortfolioConnectionRequests.query';
import publicPortfolioUserQuery from '@/components/partials/public-portfolio/PublicPortfolioHero/apollo/PublicPortfolioHero.query';
import rejectConnectionRequestMutation from '@/apollo/mutations/Portfolio/RejectConnectionRequest.mutation';
import removeConnectionMutation from '@/apollo/mutations/Portfolio/RemoveConnection.mutation';
import type { PublicPortfolioHeroData } from '@/components/partials/public-portfolio/PublicPortfolioHero/types';
import type { PortfolioConnectionRequests } from '@/components/partials/public-portfolio/PublicPortfolioConnectionActions/types';

const WEB_PLATFORM = 'WEB';

export default defineComponent({
  name: 'PublicPortfolioConnectionActionsMixin',
  apollo: {
    portfolioConnectionRequests: {
      query: portfolioConnectionRequestsQuery,
      variables() {
        return {
          slug: this.$route.params.slug,
        };
      },
      skip() {
        return !this.$auth.loggedIn || this.$auth.user.slug === this.$route.params.slug;
      },
    },
  },
  data(): { portfolioConnectionRequests: PortfolioConnectionRequests } {
    return {
      portfolioConnectionRequests: {
        connected: true,
        incomingConnectionRequest: null,
        outgoingConnectionRequest: null,
      },
    };
  },
  computed: {
    ...mapState('portfolio', ['user']),
    firstName(): string {
      return this.user?.firstname.trim() || '';
    },
    isConnected(): boolean {
      return this.portfolioConnectionRequests.connected;
    },
    isRequesterView(): boolean {
      return Boolean(!this.isConnected && !this.portfolioConnectionRequests.incomingConnectionRequest);
    },
    isReceiverView(): boolean {
      return Boolean(!this.isConnected && this.portfolioConnectionRequests.incomingConnectionRequest);
    },
    isRequestSent(): boolean {
      return this.portfolioConnectionRequests.outgoingConnectionRequest?.status === 'pending';
    },
  },
  methods: {
    async sendConnectionRequest(): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: createConnectionRequestMutation,
          variables: {
            input: { slug: this.$route.params.slug },
          },
          update: (store, { data: { outgoingConnectionRequest } }) => {
            const query = {
              query: portfolioConnectionRequestsQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const data = store.readQuery<{ portfolioConnectionRequests: PortfolioConnectionRequests }>(query);

            if (!data) return;

            data.portfolioConnectionRequests = {
              ...data.portfolioConnectionRequests,
              outgoingConnectionRequest,
            };

            store.writeQuery({ ...query, data });
          },
        });
        this.$cancelableNotice({ message: 'Connection request sent!' });
      } catch (e) {
        this.$notice.error('Failed to send connection request, try again in few minutes.', e);
        this.$sentryUtils.error(e);
      }
    },
    async cancelConnectionRequest(): Promise<void> {
      try {
        if (!this.portfolioConnectionRequests.outgoingConnectionRequest) return;

        await this.$apollo.mutate({
          mutation: cancelConnectionRequestMutation,
          variables: {
            input: { id: this.portfolioConnectionRequests.outgoingConnectionRequest.id },
          },
          update: store => {
            const query = {
              query: portfolioConnectionRequestsQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const data = store.readQuery<{ portfolioConnectionRequests: PortfolioConnectionRequests }>(query);

            if (!data) return;

            data.portfolioConnectionRequests = {
              ...data.portfolioConnectionRequests,
              outgoingConnectionRequest: null,
            };

            store.writeQuery({ ...query, data });
          },
        });
        this.$cancelableNotice({ message: 'Connection request was withdrawed' });
      } catch (e) {
        this.$notice.error('Failed to cancel connection request, try again in few minutes.', e);
        this.$sentryUtils.error(e);
      }
    },
    async acceptConnectionRequest(): Promise<void> {
      try {
        if (!this.portfolioConnectionRequests.incomingConnectionRequest) return;

        await this.$apollo.mutate({
          mutation: acceptConnectionRequestMutation,
          variables: {
            input: { id: this.portfolioConnectionRequests.incomingConnectionRequest.id },
          },
          refetchQueries: [
            { query: connectionsQuery },
            {
              query: currentUserUnseenNotificationsQuery,
              variables: {
                platform: WEB_PLATFORM,
              },
            },
          ],
          update: store => {
            const connectionQuery = {
              query: portfolioConnectionRequestsQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const connectionData = store.readQuery<{ portfolioConnectionRequests: PortfolioConnectionRequests }>(
              connectionQuery,
            );

            if (connectionData) {
              connectionData.portfolioConnectionRequests = {
                ...connectionData.portfolioConnectionRequests,
                incomingConnectionRequest: null,
                connected: true,
              };

              store.writeQuery({ ...connectionQuery, data: connectionData });
            }

            const portfolioQuery = {
              query: publicPortfolioUserQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const portfolioData = store.readQuery<{ publicPortfolioUser: PublicPortfolioHeroData }>(portfolioQuery);

            if (portfolioData) {
              portfolioData.publicPortfolioUser = {
                ...portfolioData.publicPortfolioUser,
                connections: {
                  ...portfolioData.publicPortfolioUser.connections,
                  totalCount: portfolioData.publicPortfolioUser.connections.totalCount + 1,
                },
              };

              store.writeQuery({ ...portfolioQuery, data: portfolioData });
            }
          },
        });
        this.$cancelableNotice({ message: `${this.firstName}'s request to connect was accepted` });
      } catch (e) {
        this.$notice.error('Failed to accept connection request, try again in few minutes.', e);
        this.$sentryUtils.error(e);
      }
    },
    async declineConnectionRequest(): Promise<void> {
      try {
        if (!this.portfolioConnectionRequests.incomingConnectionRequest) return;

        await this.$apollo.mutate({
          mutation: rejectConnectionRequestMutation,
          variables: {
            input: { id: this.portfolioConnectionRequests.incomingConnectionRequest.id },
          },
          refetchQueries: [
            { query: connectionsQuery },
            {
              query: currentUserUnseenNotificationsQuery,
              variables: {
                platform: WEB_PLATFORM,
              },
            },
          ],
          update: store => {
            const query = {
              query: portfolioConnectionRequestsQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const data = store.readQuery<{ portfolioConnectionRequests: PortfolioConnectionRequests }>(query);

            if (!data) return;

            data.portfolioConnectionRequests = {
              ...data.portfolioConnectionRequests,
              incomingConnectionRequest: null,
            };

            store.writeQuery({ ...query, data });
          },
        });
        this.$cancelableNotice({ message: 'Connection request was declined' });
      } catch (e) {
        this.$notice.error('Failed to decline connection request, try again in few minutes.', e);
        this.$sentryUtils.error(e);
      }
    },
    async removeConnection(): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: removeConnectionMutation,
          variables: {
            input: { slug: this.$route.params.slug },
          },
          update: store => {
            const connectionQuery = {
              query: portfolioConnectionRequestsQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const connectionData = store.readQuery<{ portfolioConnectionRequests: PortfolioConnectionRequests }>(
              connectionQuery,
            );

            if (connectionData) {
              connectionData.portfolioConnectionRequests = {
                ...connectionData.portfolioConnectionRequests,
                connected: false,
                incomingConnectionRequest: null,
                outgoingConnectionRequest: null,
              };

              store.writeQuery({ ...connectionQuery, data: connectionData });
            }

            const portfolioQuery = {
              query: publicPortfolioUserQuery,
              variables: {
                slug: this.$route.params.slug,
              },
            };

            const portfolioData = store.readQuery<{ publicPortfolioUser: PublicPortfolioHeroData }>(portfolioQuery);

            if (portfolioData) {
              portfolioData.publicPortfolioUser = {
                ...portfolioData.publicPortfolioUser,
                connections: {
                  ...portfolioData.publicPortfolioUser.connections,
                  totalCount: portfolioData.publicPortfolioUser.connections.totalCount - 1,
                },
              };

              store.writeQuery({ ...portfolioQuery, data: portfolioData });
            }
          },
        });
        this.$cancelableNotice({ message: `${this.firstName} was removed from your connections` });
      } catch (e) {
        this.$notice.error('Failed to remove connection, try again in few minutes.', e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
